/**
 * Dashboard Store Actions
 * All dashboard state mutations go through here
 *
 * Part of Phase 2: State Management Consolidation
 * @version 1.1.0
 */

let Store = null;

function getStore() {
  if (!Store) {
    Store = window.Store;
  }
  if (!Store) {
    console.warn('[DashboardActions] Store not available yet');
  }
  return Store;
}

function safeSet(path, value) {
  const store = getStore();
  if (store) {
    store.set(path, value);
    return true;
  }
  return false;
}

function safeGet(path, defaultValue) {
  const store = getStore();
  return store ? store.get(path) : defaultValue;
}

function safeUpdate(path, partial) {
  const store = getStore();
  if (store) {
    store.update(path, partial);
    return true;
  }
  return false;
}

// ============================================================================
// IDEAS
// ============================================================================

/**
 * Set dashboard ideas
 * @param {import('../../core/models/idea.js').Idea[]} ideas
 */
export function setIdeas(ideas) {
  safeSet('dashboard.ideas', Array.isArray(ideas) ? ideas : []);
}

/**
 * Add or update idea in dashboard
 * @param {import('../../core/models/idea.js').Idea} idea
 */
export function upsertIdea(idea) {
  const ideas = safeGet('dashboard.ideas', []);
  const index = ideas.findIndex(i => i.id === idea.id);
  if (index !== -1) {
    const newIdeas = [...ideas];
    newIdeas[index] = idea;
    safeSet('dashboard.ideas', newIdeas);
  } else {
    safeSet('dashboard.ideas', [...ideas, idea]);
  }
}

/**
 * Update specific idea in dashboard
 * @param {string} ideaId
 * @param {Object} updates
 */
export function updateIdea(ideaId, updates) {
  const ideas = safeGet('dashboard.ideas', []);
  const index = ideas.findIndex(i => i.id === ideaId);
  if (index !== -1) {
    const newIdeas = [...ideas];
    newIdeas[index] = { ...newIdeas[index], ...updates };
    safeSet('dashboard.ideas', newIdeas);
  }
}

/**
 * Remove idea from dashboard
 * @param {string} ideaId
 */
export function removeIdea(ideaId) {
  const ideas = safeGet('dashboard.ideas', []);
  safeSet('dashboard.ideas', ideas.filter(i => i.id !== ideaId));
}

/**
 * Remove multiple ideas from dashboard
 * @param {string[]} ideaIds
 */
export function removeIdeas(ideaIds) {
  const ideas = safeGet('dashboard.ideas', []);
  const idSet = new Set(ideaIds);
  safeSet('dashboard.ideas', ideas.filter(i => !idSet.has(i.id)));
}

// ============================================================================
// SESSIONS
// ============================================================================

/**
 * Set sessions
 * @param {import('../../core/models/session.js').Session[]} sessions
 */
export function setSessions(sessions) {
  safeSet('dashboard.sessions', Array.isArray(sessions) ? sessions : []);
}

/**
 * Add or update session
 * @param {Object} session
 */
export function upsertSession(session) {
  const sessions = safeGet('dashboard.sessions', []);
  const index = sessions.findIndex(s => s.id === session.id);
  if (index !== -1) {
    const newSessions = [...sessions];
    newSessions[index] = session;
    safeSet('dashboard.sessions', newSessions);
  } else {
    safeSet('dashboard.sessions', [session, ...sessions]);
  }
}

/**
 * Remove session
 * @param {string} sessionId
 */
export function removeSession(sessionId) {
  const sessions = safeGet('dashboard.sessions', []);
  safeSet('dashboard.sessions', sessions.filter(s => s.id !== sessionId));
}

/**
 * Set current session
 * @param {string|null} sessionId
 */
export function setCurrentSession(sessionId) {
  safeSet('dashboard.currentSessionId', sessionId);
}

// ============================================================================
// BRANDS
// ============================================================================

/**
 * Set brands
 * @param {Object[]} brands
 */
export function setBrands(brands) {
  safeSet('dashboard.brands', Array.isArray(brands) ? brands : []);
}

/**
 * Add or update brand
 * @param {Object} brand
 */
export function upsertBrand(brand) {
  const brands = safeGet('dashboard.brands', []);
  const index = brands.findIndex(b => b.id === brand.id);
  if (index !== -1) {
    const newBrands = [...brands];
    newBrands[index] = brand;
    safeSet('dashboard.brands', newBrands);
  } else {
    safeSet('dashboard.brands', [...brands, brand]);
  }
}

/**
 * Remove brand
 * @param {string} brandId
 */
export function removeBrand(brandId) {
  const brands = safeGet('dashboard.brands', []);
  safeSet('dashboard.brands', brands.filter(b => b.id !== brandId));
}

// ============================================================================
// FILTERS
// ============================================================================

/**
 * Set filter value
 * @param {string} key - format|saved|search
 * @param {any} value
 */
export function setFilter(key, value) {
  const filters = safeGet('dashboard.filters', {});
  safeSet('dashboard.filters', { ...filters, [key]: value });
}

/**
 * Set multiple filters
 * @param {Object} filters
 */
export function setFilters(filters) {
  safeUpdate('dashboard.filters', filters);
}

/**
 * Clear all filters
 */
export function clearFilters() {
  safeSet('dashboard.filters', { format: 'all', saved: false, search: '' });
}

// ============================================================================
// SELECTION
// ============================================================================

/**
 * Set selected idea IDs (for multi-select)
 * @param {string[]} ids
 */
export function setSelectedIds(ids) {
  safeSet('dashboard.selectedIds', Array.isArray(ids) ? ids : []);
}

/**
 * Toggle idea selection
 * @param {string} ideaId
 */
export function toggleSelection(ideaId) {
  const selected = safeGet('dashboard.selectedIds', []);
  if (selected.includes(ideaId)) {
    safeSet('dashboard.selectedIds', selected.filter(id => id !== ideaId));
  } else {
    safeSet('dashboard.selectedIds', [...selected, ideaId]);
  }
}

/**
 * Select all ideas
 * @param {string[]} ideaIds
 */
export function selectAll(ideaIds) {
  safeSet('dashboard.selectedIds', [...ideaIds]);
}

/**
 * Clear selection
 */
export function clearSelection() {
  safeSet('dashboard.selectedIds', []);
}

// ============================================================================
// LOADING STATE
// ============================================================================

/**
 * Set loading state
 * @param {boolean} loading
 */
export function setLoading(loading) {
  safeSet('dashboard.loading', loading);
}

/**
 * Set error state
 * @param {string|null} error
 */
export function setError(error) {
  safeSet('dashboard.error', error);
}

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize actions - register with Store
 */
export function initActions() {
  const store = getStore();
  if (!store) {
    console.warn('[DashboardActions] Store not available yet');
    return;
  }

  // Ideas
  store.registerAction('dashboard/setIdeas', setIdeas);
  store.registerAction('dashboard/upsertIdea', upsertIdea);
  store.registerAction('dashboard/updateIdea', updateIdea);
  store.registerAction('dashboard/removeIdea', removeIdea);
  store.registerAction('dashboard/removeIdeas', removeIdeas);

  // Sessions
  store.registerAction('dashboard/setSessions', setSessions);
  store.registerAction('dashboard/upsertSession', upsertSession);
  store.registerAction('dashboard/removeSession', removeSession);
  store.registerAction('dashboard/setCurrentSession', setCurrentSession);

  // Brands
  store.registerAction('dashboard/setBrands', setBrands);
  store.registerAction('dashboard/upsertBrand', upsertBrand);
  store.registerAction('dashboard/removeBrand', removeBrand);

  // Filters
  store.registerAction('dashboard/setFilter', setFilter);
  store.registerAction('dashboard/setFilters', setFilters);
  store.registerAction('dashboard/clearFilters', clearFilters);

  // Selection
  store.registerAction('dashboard/setSelectedIds', setSelectedIds);
  store.registerAction('dashboard/toggleSelection', toggleSelection);
  store.registerAction('dashboard/selectAll', selectAll);
  store.registerAction('dashboard/clearSelection', clearSelection);

  // Loading
  store.registerAction('dashboard/setLoading', setLoading);
  store.registerAction('dashboard/setError', setError);

  console.log('[DashboardActions] Registered with Store');
}

export default {
  // Ideas
  setIdeas,
  upsertIdea,
  updateIdea,
  removeIdea,
  removeIdeas,

  // Sessions
  setSessions,
  upsertSession,
  removeSession,
  setCurrentSession,

  // Brands
  setBrands,
  upsertBrand,
  removeBrand,

  // Filters
  setFilter,
  setFilters,
  clearFilters,

  // Selection
  setSelectedIds,
  toggleSelection,
  selectAll,
  clearSelection,

  // Loading
  setLoading,
  setError,

  initActions
};
